import type { BlocksContent } from '@strapi/blocks-react-renderer';

/**
 * Flattens a Strapi `blocks` tree to plain text.
 *
 * Written for `FAQPage` JSON-LD, whose `acceptedAnswer.text` must be the item
 * body with HTML stripped (10.03.03.02 AC 4). Deliberately not a generic
 * "render to string": the joining rules below are what reads correctly as a
 * spoken answer, which is what a rich-text-to-prose flattening is for.
 *
 * Rules:
 * - inline leaves inside one block are concatenated with **no** separator, so
 *   bold/italic runs rejoin seamlessly (`**Ma**the` → `Mathe`);
 * - a **link** contributes only its visible text — the `url` is dropped;
 * - **list items** are joined with `", "` (`Mathe`, `Deutsch` → `Mathe, Deutsch`);
 * - **block-level** nodes (paragraph, heading, list, quote) are joined with a
 *   single space;
 * - runs of whitespace are collapsed and the result trimmed.
 */

type Node = {
  type?: string;
  text?: string;
  children?: Node[];
};

/** Concatenates a node's inline descendants with no separator. */
function inlineText(node: Node): string {
  if (typeof node.text === 'string') {
    return node.text;
  }
  // A link node's `url` is intentionally ignored — only its children count.
  return (node.children ?? []).map(inlineText).join('');
}

/** Flattens one block-level node, applying the list-item rule. */
function blockText(node: Node): string {
  if (node.type === 'list') {
    return (node.children ?? []).map(blockText).filter(Boolean).join(', ');
  }
  // `list-item` and every other block (paragraph, heading, quote) flatten
  // through their inline children.
  return inlineText(node);
}

export function blocksToPlainText(nodes?: BlocksContent): string {
  if (!nodes) {
    return '';
  }

  return (nodes as unknown as Node[])
    .map(blockText)
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}
